import React, { useEffect, useRef, useState } from "react";
import { avanzoApi } from "@/lib/avanzoApi";

/**
 * BuddyTutorCard — Fase 8.2 Guided Problem Solving.
 * Estados: OFFER → THINK/METACOG → HINT → GUIDED_STEP → EXPLANATION
 *          → CHECK_UNDERSTANDING → COMPLETE
 *
 * NO fullscreen: se ancla al reactivo. Estudiante puede omitir siempre.
 * NO chat abierto: máx 6 turnos por problema (cap en backend).
 * NO transcript: el backend sólo persiste señales.
 *
 * Props:
 *   itemId, trainingSessionId, subject, onFinished (fn)
 *   mode: "OFFER" (muestra oferta inicial post-error) o "DIRECT" (arranca)
 */
export default function BuddyTutorCard({
  itemId, trainingSessionId, subject, onFinished, initialTrigger = "post_incorrect",
}) {
  const [state, setState] = useState("OFFER");
  const [tsid, setTsid] = useState(null);
  const [payload, setPayload] = useState(null);
  const [freeText, setFreeText] = useState("");
  const [helpfulSent, setHelpfulSent] = useState(null);
  const [pending, setPending] = useState(false);
  const finishedRef = useRef(false);

  const dismiss = async (reason = "dismissed") => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (tsid) {
      try { await avanzoApi.tutorFinish({ tutor_session_id: tsid, reason }); }
      catch (_) { /* silencioso */ }
    }
    onFinished?.();
  };

  const startFull = async (askFor = null) => {
    setPending(true);
    try {
      const r = askFor === "hint"
        ? await avanzoApi.tutorHint({
            item_id: itemId, training_session_id: trainingSessionId,
          })
        : await avanzoApi.tutorStart({
            item_id: itemId, training_session_id: trainingSessionId,
            trigger_reason: initialTrigger,
          });
      const d = r.data;
      if (!d?.tutor_session_id) throw new Error("no_session");
      setTsid(d.tutor_session_id);
      setPayload(d);
      setState(d.stage || "HINT");
    } catch (_) {
      onFinished?.();
    } finally {
      setPending(false);
    }
  };

  const advance = async (askFor) => {
    if (!tsid) return;
    setPending(true);
    try {
      const clean = (freeText || "").trim().slice(0, 300);
      const r = await avanzoApi.tutorRespond({
        tutor_session_id: tsid,
        text: clean || undefined,
        ask_for: askFor || undefined,
      });
      setFreeText("");
      const d = r.data;
      if (d?.completed) { await dismiss("completed"); return; }
      setPayload(d);
      setState(d.stage || state);
    } catch (_) { /* silencioso */ }
    finally { setPending(false); }
  };

  const finishWith = async (resolved, understood) => {
    if (!tsid) { onFinished?.(); return; }
    try {
      await avanzoApi.tutorFinish({
        tutor_session_id: tsid, resolved, understood,
        reason: resolved ? "resolved" : "closed",
      });
    } catch (_) { /* silencioso */ }
    onFinished?.();
  };

  const handleHelpful = async (v) => {
    if (helpfulSent !== null || !tsid) return;
    setHelpfulSent(v);
    try { await avanzoApi.tutorHelpful({ tutor_session_id: tsid, helpful: v }); }
    catch (_) { /* silencioso */ }
  };

  // OFFER
  if (state === "OFFER") {
    return (
      <div className="avz-card avz-mt-6"
           data-testid="avz-tutor-offer"
           style={{ borderLeft: `4px solid var(--avz-subject-${subject || "matematica"})` }}>
        <div className="avz-row-between" style={{ marginBottom: 4 }}>
          <span className="avz-chip">Este ejercicio parece necesitar un poco más de análisis.</span>
          <button type="button" className="avz-btn avz-btn-ghost avz-btn-sm"
                  onClick={() => dismiss("declined_offer")}
                  data-testid="avz-tutor-decline">
            Seguir practicando
          </button>
        </div>
        <p className="avz-lead avz-mt-4">
          Puedo ayudarte a pensarlo paso a paso. ¿Quieres que trabajemos juntos este problema?
        </p>
        <div className="avz-row avz-mt-4" style={{ gap: 8 }}>
          <button type="button" className="avz-btn avz-btn-primary"
                  onClick={() => startFull()}
                  disabled={pending}
                  data-testid="avz-tutor-accept">
            Resolverlo con Buddy
          </button>
          <button type="button" className="avz-btn avz-btn-secondary"
                  onClick={() => startFull("hint")}
                  disabled={pending}
                  data-testid="avz-tutor-hint-only">
            Ver una pista
          </button>
        </div>
      </div>
    );
  }

  // Session activa
  const isCheck = state === "CHECK_UNDERSTANDING";
  const isComplete = state === "COMPLETE_EXPLANATION" || state === "WORKED_PARTIAL";

  return (
    <div className="avz-card avz-mt-6"
         data-testid="avz-tutor-card"
         data-stage={state}
         data-tutor-session-id={tsid}
         style={{ borderLeft: `4px solid var(--avz-subject-${subject || "matematica"})` }}>
      <div className="avz-row-between" style={{ marginBottom: 4 }}>
        <span className="avz-chip" data-testid="avz-tutor-stage">
          Vamos a trabajar juntos
        </span>
        <button type="button" className="avz-btn avz-btn-ghost avz-btn-sm"
                onClick={() => dismiss("closed")}
                data-testid="avz-tutor-close">
          Cerrar
        </button>
      </div>

      <p className="avz-lead avz-mt-4" data-testid="avz-tutor-message">
        {payload?.message || "…"}
      </p>

      {isCheck && Array.isArray(payload?.quick_options) && (
        <div className="avz-mt-4" data-testid="avz-tutor-quick-options">
          {payload.quick_options.map((opt, i) => (
            <button key={i} type="button"
                    className="avz-option"
                    onClick={() => finishWith(true, true)}
                    data-testid={`avz-tutor-quick-${i}`}>
              <span>{opt}</span>
            </button>
          ))}
        </div>
      )}

      {!isCheck && (
        <div className="avz-mt-4">
          <textarea className="avz-input"
                    rows={2} maxLength={300}
                    placeholder="Escribe tu razonamiento (opcional)"
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    data-testid="avz-tutor-textarea"/>
          <div className="avz-row-between avz-mt-4">
            <span className="avz-mute" data-testid="avz-tutor-count">
              {freeText.length}/300
            </span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button type="button" className="avz-btn avz-btn-ghost avz-btn-sm"
                      onClick={() => advance("hint")}
                      disabled={pending}
                      data-testid="avz-tutor-more-hint">
                Otra pista
              </button>
              <button type="button" className="avz-btn avz-btn-secondary avz-btn-sm"
                      onClick={() => advance()}
                      disabled={pending}
                      data-testid="avz-tutor-next">
                Continuar
              </button>
              <button type="button" className="avz-btn avz-btn-primary avz-btn-sm"
                      onClick={() => advance("check")}
                      disabled={pending}
                      data-testid="avz-tutor-ready">
                Ya lo entendí
              </button>
            </div>
          </div>
        </div>
      )}

      {isCheck && (
        <div className="avz-mt-4" data-testid="avz-tutor-check-buttons">
          <div className="avz-row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="avz-btn avz-btn-primary"
                    onClick={() => finishWith(true, true)}
                    data-testid="avz-tutor-finish-resolved">
              Sí, quedó claro
            </button>
            <button type="button" className="avz-btn avz-btn-secondary"
                    onClick={() => advance("explanation")}
                    data-testid="avz-tutor-need-more">
              Necesito otra explicación
            </button>
          </div>
        </div>
      )}

      {helpfulSent === null && payload && (
        <div className="avz-row avz-mt-4" style={{ gap: 8 }}>
          <span className="avz-mute" style={{ marginRight: 4 }}>¿Fue útil?</span>
          <button type="button" className="avz-btn avz-btn-ghost avz-btn-sm"
                  onClick={() => handleHelpful(true)}
                  data-testid="avz-tutor-helpful-yes">
            Sí
          </button>
          <button type="button" className="avz-btn avz-btn-ghost avz-btn-sm"
                  onClick={() => handleHelpful(false)}
                  data-testid="avz-tutor-helpful-no">
            No
          </button>
        </div>
      )}
    </div>
  );
}
